<script lang="ts" setup>
import { Document } from '@contentful/rich-text-types'
import type { EntryCollection } from 'contentful'
import { isStoreOpenNow } from '~/helpers/stores'

const locale = useI18n().locale.value
const userStore = useUserStore()
const shopStore = useShopStore()
const isShowFeedback = locale !== 'fr'

const isMyStoreOpen = computed(() => {
  const myStore = shopStore.myStore
  if (!myStore) return false

  return isStoreOpenNow(myStore, locale)
})

const todayStoreCloseTime = computed(() => {
  const day = new Date().toLocaleString('en-US', { weekday: 'long' })
  const closes: string | undefined = shopStore.myStore?.[`c_storeHours${day}End`]
  return formatStoreTime(closes)
})
const { data } = await useAsyncData('siteFooter', handler, { transform, deep: false })

async function handler() {
  const entries = await useContentful().getEntries({
    content_type: 'siteFooter',
    'fields.locale': localeConfig[locale].code,
    locale: localeConfig[locale].code,
    include: 8,
  })
  return entries
}

function transform(entries: EntryCollection<any> | undefined) {
  if (!entries || !entries.items.length) return {}
  const { items, includes } = entries
  const trustwell = mapEntries(items, includes?.Entry, 'trustwell', 'actions')
  const footerNavigation = mapEntries(items, includes?.Entry, 'footerNavigation', 'panels')
  const paymentIcons = mapEntries(items, includes?.Entry, 'paymentIcons')
  // SAFETY: the siteFooter footerSignUpPanel field is a Contentful rich text field, so its value is a Document.
  const signUpPanel = mapEntries(items, includes?.Entry, 'footerSignUpPanel')?.[0]?.fields as Document
  return { trustwell, footerNavigation, paymentIcons, signUpPanel }
}
</script>

<template>
  <footer>
    <Trustwell :data="data?.trustwell" />

    <div class="footer__brandmark">
      <svg-icon name="mhj:brandmark" />
    </div>

    <div class="footer__body container">
      <div class="footer__body-navi">
        <FooterNav :data="data?.footerNavigation" />
      </div>

      <div class="footer__body-subscribe">
        <BrillianceSignup v-if="!userStore.isLoggedIn" :signUpPanel="data?.signUpPanel" />

        <div class="footer__my-store-container">
          <!-- Show selected store details (name, hours, phone) in footer -->
          <div v-if="shopStore.myStore" class="footer__my-store">
            <div class="footer__my-store-header">
              <svg-icon name="mhj:set-my-store-dark" class="footer__my-store-icon" />
              <span>{{ $t('set_my_store.header_my_store') }}</span>
            </div>
            <p class="footer__my-store-name">{{ tidyStoreName(shopStore.myStore.name) }}</p>
            <p v-if="todayStoreCloseTime && isMyStoreOpen" class="footer__my-store-status">
              {{ $t('store_page.store_open') }} {{ todayStoreCloseTime }}
            </p>
            <div v-if="shopStore.myStore.phone" class="footer__my-store-phone">
              <span>Ph: </span
              ><a class="store-address__link" :href="`tel:${shopStore.myStore.phone}`">{{ shopStore.myStore.phone }}</a>
            </div>
          </div>

          <!-- CTA opens the Set My Store slide-in-panel -->
          <Action action="setMyStore" variant="none" class="footer__my-store-action">
            <span class="footer__my-store-action-content">
              <svg-icon v-if="!shopStore.myStore" name="mhj:set-my-store-dark" class="footer__my-store-action-icon" />
              <span :class="{ 'footer__my-store-action-text--selected': !!shopStore.myStore }">
                {{ $t(shopStore.myStore ? 'store_page.more_details_link' : 'set_my_store.header_set_my_store') }}
              </span>
            </span>
          </Action>
        </div>

        <SocialLinks />
      </div>

      <Feedback v-if="isShowFeedback" class="footer__body-feedback" />
    </div>

    <hr />

    <div class="footer__bottom container">
      <div class="footer__bottom-payment-info">
        <PaymentIcons :data="data?.paymentIcons" />

        <div class="footer__bottom-payment-text">
          {{ $t('footer.payment_info.information') }}
          <Anchor url="/finance-options" target="_self" class="footer__bottom-payment-info-link">
            {{ $t('footer.payment_info.link') }}
          </Anchor>
        </div>
      </div>

      <Disclaimer />
    </div>
  </footer>
</template>

<style scoped lang="postcss">
footer {
  margin-bottom: var(--space-70);
  border-top: 1px solid var(--border-dark-10);

  hr {
    display: block;
    margin: var(--space-60) var(--space-50);
    border-top: 1px solid var(--border-dark-10);
  }

  .footer__brandmark {
    background: var(--surface-dark-10);
    padding-block: var(--space-60);
    display: flex;
    justify-content: center;

    svg {
      width: var(--space-60);
      height: var(--space-60);
      color: var(--text-reverse);
    }
  }

  .footer__body {
    overflow: hidden;
    padding: var(--space-70) var(--space-50) 0 var(--space-50);

    .footer__body-subscribe {
      display: flex;
      flex-direction: column;
      gap: var(--space-70);
    }

    .footer__my-store-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-top: 1px solid var(--border-disabled);
      border-bottom: 1px solid var(--border-disabled);
      padding: var(--space-60) 0;
      gap: var(--space-60);
      width: 100%;
      max-width: var(--text-max-width);
    }

    .footer__my-store-action {
      width: fit-content;
    }

    .footer__my-store-action-content {
      display: inline-flex;
      align-items: center;
      gap: var(--space-30);
      font-size: var(--text-base);
      line-height: var(--line-height-default);
      font-weight: 400;
      letter-spacing: 0.28px;
    }

    .footer__my-store-action-icon {
      width: var(--space-50);
      height: var(--space-50);
      display: block;
      flex-shrink: 0;
    }

    .footer__my-store {
      display: flex;
      flex-direction: column;
      gap: var(--space-20);
    }

    .footer__my-store-header {
      display: flex;
      align-items: center;
      gap: var(--space-30);
      font-size: var(--text-small);
      line-height: var(--line-height-default);
      font-weight: 400;
      letter-spacing: 0.24px;
    }

    .footer__my-store-icon {
      width: var(--space-50);
      height: var(--space-50);
      display: block;
      flex-shrink: 0;
    }

    .footer__my-store-name {
      font-size: var(--text-large);
      font-weight: 700;
      line-height: var(--line-height-default);
      letter-spacing: var(--letter-spacing-default);
      margin-bottom: 0;
    }

    .footer__my-store-status,
    .footer__my-store-phone {
      font-size: var(--text-base);
      line-height: var(--line-height-default);
      font-weight: 400;
      letter-spacing: 0.28px;
      margin-bottom: 0;
    }

    .footer__my-store-action-text--selected {
      text-decoration: underline;
    }
  }

  .footer__bottom-payment-info {
    display: flex;
    flex-direction: column;
    font-size: var(--text-xsmall);
    gap: var(--space-40);
    width: 100%;
    margin-bottom: var(--space-50);
  }

  @media (--desktop) {
    .footer__body {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));

      .footer__body-subscribe {
        grid-column: 1;
        grid-row: 1;
      }

      .footer__body-navi {
        grid-column: 2;
        grid-row: 1;
      }
    }

    .footer__bottom {
      display: grid;
      grid-template-areas: 'left right';
      gap: var(--space-70);

      .footer__bottom-payment-info {
        grid-area: right;
        align-items: flex-end;
      }
    }
  }

  @media (--mobile-tablet-only) {
    .footer__body-subscribe,
    .footer__body-navi {
      display: flex;
      max-width: 40rem;
      margin: auto;
    }

    .footer__body-feedback {
      max-width: 40rem;
      margin: var(--space-40) auto 0;
    }
  }
}
</style>
